package dev.kuml.portal.web.projects

import dev.kuml.portal.web.AppScope
import dev.kuml.portal.web.activity.activitySection
import dev.kuml.portal.web.api.ApiResult
import dev.kuml.portal.web.api.PortalApi
import dev.kuml.portal.web.api.ProjectResponse
import dev.kuml.portal.web.components.emailVerificationBanner
import dev.kuml.portal.web.error.ErrorCopy
import dev.kuml.portal.web.i18n.t
import dev.kuml.portal.web.util.DateFormat
import io.kvision.core.Container
import io.kvision.core.onClick
import io.kvision.form.text.TextInput
import io.kvision.form.text.textInput
import io.kvision.html.Button
import io.kvision.html.Div
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.span
import kotlinx.coroutines.launch

/** The provisional name every new project starts with (mirrors `ProjectService`'s default on the
 * server) -- used both to decide when to auto-rename after a first generation ([dev.kuml.portal.web.editor])
 * and to pick which cards get the mustFix 3 visual-distinguishability treatment below.
 *
 * R-18: NEVER translate this constant -- it is sent to the server via `api.createProject(...)`
 * and persisted there, and it is simultaneously a COMPARISON KEY (`project.name ==
 * DEFAULT_PROJECT_NAME` drives the metaline below and `EditorScreen.applyAutoName`). Its
 * DISPLAY-ONLY, language-dependent presentation lives in [DefaultNameDisplay.projectName]. */
public const val DEFAULT_PROJECT_NAME: String = "Unbenanntes Projekt"

/**
 * Project list (`/projects`, design spec section 2) -- the home screen after login. Card grid with
 * Umbenennen/Loeschen, "+ Neues Projekt" (creates with the provisional default name and navigates
 * straight into the editor), an inviting empty state, and a disabled-with-explanation `+`-button at
 * the plan's project limit (mustFix 3's visual-distinguishability guarantee for still-default-named
 * cards lives in [renderProjectCard]/[defaultNameIndices]).
 */
public fun Container.projectListScreen(
    api: PortalApi,
    onOpenProject: (String) -> Unit,
) {
    lateinit var newProjectButton: Button
    lateinit var newProjectHint: Div
    lateinit var gridHost: Div
    lateinit var statusDiv: Div

    var projectsLimit: Int? = null
    var creatingNewProject = false
    var currentProjectsUsed = 0

    fun updateNewProjectButtonState(projectsUsed: Int) {
        val limit = projectsLimit
        if (limit != null && projectsUsed >= limit) {
            newProjectButton.disabled = true
            newProjectHint.content = t.projects.projectLimitReached(limit)
        } else {
            newProjectButton.disabled = creatingNewProject
            newProjectHint.content = ""
        }
    }

    fun reload() {
        AppScope.launch {
            val usageResult = api.usage()
            if (usageResult is ApiResult.Success) {
                projectsLimit = usageResult.value.projectsLimit
            }

            gridHost.removeAll()
            gridHost.addCssClass("kportal-grid")
            repeat(3) { gridHost.div(className = "kportal-skeleton-card") }

            when (val result = api.listProjects()) {
                is ApiResult.Success -> {
                    val projects = result.value
                    gridHost.removeAll()
                    if (projects.isEmpty()) {
                        gridHost.removeCssClass("kportal-grid")
                        gridHost.div(className = "kportal-empty-state") {
                            div(t.projects.emptyState)
                        }
                    } else {
                        gridHost.addCssClass("kportal-grid")
                        val indices = defaultNameIndices(projects)
                        for (project in projects) {
                            renderProjectCard(gridHost, project, indices[project.id], api, onOpenProject) { reload() }
                        }
                    }
                    currentProjectsUsed = projects.size
                    updateNewProjectButtonState(currentProjectsUsed)
                }
                is ApiResult.HttpError -> {
                    val copy =
                        ErrorCopy.forHttpStatus(
                            status = result.status,
                            serverCode = result.error?.code,
                            serverMessage = result.error?.message,
                            retryAfterSeconds = null,
                            aiLimit = null,
                            resetLabel = null,
                            strings = t.errors,
                        )
                    statusDiv.content = copy.message
                }
                ApiResult.NetworkError -> statusDiv.content = t.common.networkErrorReload
            }
        }
    }

    fun createProject() {
        if (creatingNewProject) return
        creatingNewProject = true
        newProjectButton.disabled = true
        AppScope.launch {
            when (val result = api.createProject(DEFAULT_PROJECT_NAME)) {
                is ApiResult.Success -> onOpenProject(result.value.project.id)
                is ApiResult.HttpError -> {
                    creatingNewProject = false
                    updateNewProjectButtonState(currentProjectsUsed)
                    statusDiv.content =
                        if (result.status == 402) {
                            // 402 here means the plan's PROJECT-count cap, not the AI-generation
                            // quota ErrorCopy.forHttpStatus's 402 branch is worded for (see its
                            // KDoc) -- mirror the same approved "Free-Plan: maximal N Projekte"
                            // wording already shown in the disabled-button hint, not the AI copy.
                            val limit = projectsLimit
                            if (limit != null) t.projects.projectLimitReached(limit) else t.projects.projectLimitReachedUnknown
                        } else {
                            val copy =
                                ErrorCopy.forHttpStatus(
                                    status = result.status,
                                    serverCode = result.error?.code,
                                    serverMessage = result.error?.message,
                                    retryAfterSeconds = null,
                                    aiLimit = null,
                                    resetLabel = null,
                                    strings = t.errors,
                                )
                            copy.message
                        }
                }
                ApiResult.NetworkError -> {
                    creatingNewProject = false
                    updateNewProjectButtonState(currentProjectsUsed)
                    statusDiv.content = t.common.networkErrorRetry
                }
            }
        }
    }

    // R-3: shown ABOVE the page title for a caller whose email is still unverified -- self-
    // contained (own `GET /api/auth/me` call, silent-fail), see its own KDoc.
    emailVerificationBanner(api)

    div(className = "kportal-section-head") {
        div(t.projects.pageTitle, className = "kportal-page-title")
        div {
            newProjectButton = button(t.projects.newProject, className = "kportal-btn kportal-btn-primary kportal-btn-auto")
            newProjectButton.onClick { createProject() }
        }
    }
    newProjectHint = div(className = "kportal-status-neutral")
    gridHost = div()
    statusDiv = div(className = "kportal-status-area")

    reload()

    // R-15: the caller's own activity calendar, below the project grid -- projects stay the first
    // thing the user sees on this screen. A load failure here is self-contained (see
    // activitySection's own KDoc) and never affects the project grid above it.
    activitySection(api)
}

/**
 * Running index among projects that STILL carry the provisional default name, ordered by creation
 * time -- the mustFix 3 minimum guarantee: even if two "Unbenanntes Projekt" cards were somehow
 * created in the same minute (so their "Erstellt: HH:mm" metaline would otherwise tie), the index
 * still tells them apart. Non-default-named projects are absent from the returned map.
 */
private fun defaultNameIndices(projects: List<ProjectResponse>): Map<String, Int> {
    val indices = mutableMapOf<String, Int>()
    var counter = 0
    for (project in projects.sortedBy { it.createdAt }) {
        if (project.name == DEFAULT_PROJECT_NAME) {
            counter += 1
            indices[project.id] = counter
        }
    }
    return indices
}

private fun renderProjectCard(
    host: Div,
    project: ProjectResponse,
    defaultIndex: Int?,
    api: PortalApi,
    onOpen: (String) -> Unit,
    onChanged: () -> Unit,
) {
    val card = host.div(className = "kportal-card")
    card.onClick { onOpen(project.id) }

    val displayName = DefaultNameDisplay.projectName(project.name, t.projects)
    val nameDiv = card.div(displayName, className = "kportal-card-name")

    val metaText =
        if (project.name == DEFAULT_PROJECT_NAME) {
            val time = DateFormat.timeOfDay(project.createdAt)
            if (time != null) t.projects.createdAt(time, defaultIndex) else t.projects.createdAtUnknown(defaultIndex)
        } else {
            val date = t.format.date(project.updatedAt)
            if (date != null) t.projects.lastChanged(date) else t.projects.lastChangedUnknown
        }
    card.div(metaText, className = "kportal-card-meta")

    val actions = card.div(className = "kportal-card-actions")
    val renameButton = actions.button(t.projects.renameAction, className = "kportal-icon-button")
    renameButton.setAttribute("aria-label", t.projects.renameAriaLabel)
    val deleteButton = actions.button(t.projects.deleteAction, className = "kportal-icon-button")
    deleteButton.setAttribute("aria-label", t.projects.deleteAriaLabel)

    val deletePopoverHost = card.div()

    renameButton.onClick { e ->
        e.stopPropagation()
        startInlineRename(nameDiv, project, api, onChanged)
    }

    deleteButton.onClick { e ->
        e.stopPropagation()
        showDeleteConfirmation(deletePopoverHost, project, api, onChanged)
    }
}

/** Stift -> inline textfield (Enter speichert per `PATCH`, Escape verwirft ohne Netzwerkaufruf,
 * Blur speichert) -- the design spec's secondary "Komfort"-Umbenennung directly on the card. */
private fun startInlineRename(
    nameDiv: Div,
    project: ProjectResponse,
    api: PortalApi,
    onChanged: () -> Unit,
) {
    val originalName = project.name
    val originalDisplay = DefaultNameDisplay.projectName(originalName, t.projects)
    nameDiv.content = null
    val input = nameDiv.textInput(value = originalName, className = "kportal-input")
    // Removing the focused input from the DOM (cancel's nameDiv.removeAll()) fires a native blur
    // event synchronously in real browsers, which would otherwise re-invoke save() with the
    // still-edited text and silently persist a rename the user just pressed Escape to discard.
    var settled = false

    fun revert() {
        nameDiv.removeAll()
        nameDiv.content = originalDisplay
    }

    fun cancel() {
        if (settled) return
        settled = true
        revert()
    }

    fun save() {
        if (settled) return
        settled = true
        val newName = input.value?.trim().orEmpty()
        if (newName.isEmpty() || newName == originalName) {
            revert()
            return
        }
        AppScope.launch {
            when (api.renameProject(project.id, newName)) {
                is ApiResult.Success -> onChanged()
                // Rename failed -- revert the inline field rather than leaving it displaying a
                // name the server never actually stored, with no indication anything went wrong.
                is ApiResult.HttpError, ApiResult.NetworkError -> revert()
            }
        }
    }

    input.setEventListener<TextInput> {
        keydown = { e ->
            when (e.key) {
                "Enter" -> {
                    e.preventDefault()
                    save()
                }
                "Escape" -> {
                    e.preventDefault()
                    cancel()
                }
            }
        }
        blur = { save() }
    }
}

/** The one confirmation dialog allowed anywhere in this UI (deleting is irreversible) -- a small
 * popover anchored to the card, not a screen-filling modal. */
private fun showDeleteConfirmation(
    host: Div,
    project: ProjectResponse,
    api: PortalApi,
    onChanged: () -> Unit,
) {
    host.removeAll()
    val popover = host.div(className = "kportal-popover")
    popover.onClick { e -> e.stopPropagation() }
    popover.span(t.projects.deleteConfirmQuestion(DefaultNameDisplay.projectName(project.name, t.projects)))
    val errorSpan = popover.span("", className = "kportal-status-error")
    errorSpan.visible = false
    val actions = popover.div(className = "kportal-popover-actions")
    val cancel = actions.button(t.common.cancel, className = "kportal-btn kportal-btn-secondary kportal-btn-auto")
    val confirm = actions.button(t.common.delete, className = "kportal-btn kportal-btn-text")
    cancel.onClick { e ->
        e.stopPropagation()
        host.removeAll()
    }
    confirm.onClick { e ->
        e.stopPropagation()
        confirm.disabled = true
        cancel.disabled = true
        AppScope.launch {
            when (api.deleteProject(project.id)) {
                is ApiResult.Success -> onChanged()
                // Delete failed -- surface it in the popover instead of silently closing it via
                // reload() and leaving the user to guess whether anything happened.
                is ApiResult.HttpError, ApiResult.NetworkError -> {
                    confirm.disabled = false
                    cancel.disabled = false
                    errorSpan.content = t.projects.deleteFailed
                    errorSpan.visible = true
                }
            }
        }
    }
}
